'use client';

import { useMergeRefs } from '@floating-ui/react';
import { Fragment, type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { ComboboxRootContext } from './context/combobox-root.context';
import { useComboboxChipsAndOverflow } from './hooks/use-combobox-chips-and-overflow';
import { useComboboxTriggerRenderProps } from './hooks/use-combobox-renders';
import { comboboxTriggerRecipe } from './styles/combobox-trigger.styles';
import type { ComboboxTriggerPropsInterface } from './types/combobox-trigger.types';

/**
 * @since 10.0.0
 */
export const ComboboxTrigger = forwardRef<HTMLDivElement, ComboboxTriggerPropsInterface>(
  (props: ComboboxTriggerPropsInterface, forwardedRef) => {
    const { appearance, combobox, editable, disabled, error, size } =
      useContext(ComboboxRootContext);

    const {
      renderAdornment,
      renderChip,
      renderChipsOverflow,
      renderClear,
      renderInput,
      renderInputGroup,
      ...nonRenderProps
    } = useComboboxTriggerRenderProps(props);

    const { className, css: cssProp, onClick, ...rest } = nonRenderProps;

    const styles = css(comboboxTriggerRecipe.raw({ disabled, error, size }), cssProp);

    const chipsAndOverflow = useComboboxChipsAndOverflow({
      renderChip,
      renderChipsOverflow,
    });

    const mergedRefs = useMergeRefs([combobox.refs.setPositionReference, forwardedRef]);

    const onClickCallback = useCallback<MouseEventHandler<HTMLDivElement>>(
      (event) => {
        combobox.onClickTrigger(event);
        onClick?.(event);
      },
      [combobox, onClick],
    );

    return (
      // eslint-disable-next-line eslint-plugin-jsx-a11y(click-events-have-key-events)
      <div
        ref={mergedRefs}
        data-c11n-component="Combobox.Trigger"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        onClick={disabled ? (event) => event.preventDefault() : onClickCallback}
        onMouseDown={(event) => {
          /**
           * Prevent default to avoid focus shifts when clicking other parts of the trigger,
           * but allow default behavior on input elements to enable text selection with mouse.
           */
          if ((event.target as HTMLElement).tagName !== 'INPUT') {
            event.preventDefault();
          }
        }}
        tabIndex={-1}
        {...rest}
      >
        {renderInputGroup({
          children: (
            <Fragment>
              {chipsAndOverflow}
              {renderInput({})}
            </Fragment>
          ),
        })}
        {disabled || !editable || !combobox.selectedValues.length ? null : renderClear({})}
        {appearance === 'select' ? renderAdornment({}) : null}
      </div>
    );
  },
);

ComboboxTrigger.displayName = 'Combobox.Trigger';
